// The actions at the end of the onboarding path (TX-US-0042), for the owner of the current stage
// only: the approval first, labelled by where it sends the counterparty, then Return and Reject -
// Legal's rejection final or referred to senior management - and, on a referred rejection, the
// override granted or declined; an admin's way back from Rejected. What the approval waits on is said
// beneath them. The API refuses regardless - this decides what is offered.

import { useState } from 'react';

import { transitionCounterparty } from '../../api.js';
import { useMe } from '../../MeContext.js';
import { notify } from '../../ui/notify.jsx';
import { statusLabel } from '../labels.js';
import { EndOverrideDialog, GrantOverrideDialog } from './OverrideDialogs.jsx';
import { actionsFor, moveRefusal, passedEarlierGate, waitingOn } from './path.js';
import RejectDialog from './RejectDialog.jsx';
import ReturnDialog from './ReturnDialog.jsx';

export default function PathActions({ record, sheets, onChanged }) {
  const me = useMe();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [dialog, setDialog] = useState(null);

  const offered = actionsFor(record, me, sheets.kyc?.sheet);
  const { approve, returnTo, reject, refer, grant, endOverride, reopen } = offered;
  if (!approve && !returnTo.length && !reject && !grant && !reopen) return null;

  const waiting = waitingOn(record.status, sheets);
  const passedBefore = approve && passedEarlierGate(record, me);

  const moved = (updated) => {
    setDialog(null);
    onChanged(updated);
    notify(`${updated.name} moved to ${statusLabel(updated.status)}.`);
  };

  const move = async (target) => {
    setBusy(true);
    setError(null);
    try {
      moved(await transitionCounterparty(record.counterparty_id, target, record.version));
    } catch (err) {
      setError(moveRefusal(err));
    } finally {
      setBusy(false);
    }
  };

  const dialogProps = { counterparty: record, onClose: () => setDialog(null), onMoved: moved };

  return (
    <div className="path-actions">
      <div className="status-actions-row">
        {approve && (
          <button type="button" className="primary" onClick={() => move(approve.target)}
                  disabled={busy || passedBefore || waiting.missing.length > 0}>
            {busy ? 'Updating…' : approve.label}
          </button>
        )}
        {reopen && (
          <button type="button" className="primary" onClick={() => move('PROSPECT')}
                  disabled={busy}>
            {busy ? 'Updating…' : 'Return to Prospect'}
          </button>
        )}
        {grant && (
          <button type="button" className="primary" onClick={() => setDialog('grant')}
                  disabled={busy}>
            Override granted
          </button>
        )}
        {returnTo.length > 0 && (
          <button type="button" onClick={() => setDialog('return')} disabled={busy}>Return</button>
        )}
        {reject && (
          <button type="button" className="danger" onClick={() => setDialog('reject')}
                  disabled={busy}>
            Reject
          </button>
        )}
        {endOverride && (
          <button type="button" className="danger" onClick={() => setDialog('end')}
                  disabled={busy}>
            Override declined
          </button>
        )}
      </div>
      {passedBefore && (
        <p className="hint">You passed an earlier gate on this counterparty, so someone else
          approves this one.</p>
      )}
      {!passedBefore && waiting.missing.length > 0 && (
        <p className="hint">Save the {waiting.sheet} with: {waiting.missing.join(', ')}.</p>
      )}
      {error && <p className="error">{error}</p>}

      {dialog === 'return' && <ReturnDialog {...dialogProps} targets={returnTo} />}
      {dialog === 'grant' && <GrantOverrideDialog {...dialogProps} />}
      {dialog === 'end' && <EndOverrideDialog {...dialogProps} />}
      {dialog === 'reject' && (
        <RejectDialog counterparty={record} canRefer={refer} onClose={() => setDialog(null)}
                      onRejected={moved} />
      )}
    </div>
  );
}
